import React, { ComponentProps } from "react";
import styled, {
  css,
  DefaultTheme,
  FlattenSimpleInterpolation,
  StyledComponent,
} from "styled-components";

import { glow, InfoBanner, Toolbar } from "@examspark/common-ui";

import { EditorVariant } from "../types";

//
// Ensure that CKE's styles are imported before ours so that we cascade properly
//
import "ckeditor5/ckeditor5.css";
import "./EditorStyles.css";

export const EditorWarning: StyledComponent<
  // Explicit props works around TypeScript error
  // Exported variable 'EditorWarning' has or is using name 'Props' from external module ...
  React.FC<ComponentProps<typeof InfoBanner>>,
  DefaultTheme
> = styled(InfoBanner)`
  > div {
    gap: 0;
    padding: 0.5rem;
    min-height: unset;
  }

  svg {
    height: 12px;
  }

  border-style: solid;
  border-color: var(--ck-color-base-border);
  border-radius: var(--ck-border-radius);
  background-color: hsl(246.79deg 85% 95%);

  ${Toolbar} {
    width: 100%;
  }
`;

//
// Manage the styling of the editor here.
// When trying to control the editor's styling, make sure to explore the CSS
// in an inspector to see whether there are variables that can control the behavior.
//
// Be mindful of the seamless shared toolbar; it does not render in the editor's
// component tree, so if you need to apply styling to it, you need to put those
// selectors in EditorStyles.css.
//

const EditorStyles = styled.div<{
  variant: EditorVariant;
  isFocused: boolean;
  extraStyles?: FlattenSimpleInterpolation | FlattenSimpleInterpolation[];
}>`
  position: relative;

  ${(props) => props.extraStyles}

  ${EditorWarning} {
    ${({ variant }) =>
      variant === "seamless"
        ? css`
            border-width: 1px 0 0 0;
          `
        : css`
            border-width: 1px;
            margin-top: 1rem;
            position: absolute;
            z-index: 10;
            width: 100%;
            box-shadow: var(--ck-balloon-arrow-drop-shadow);
          `}

    ${({ isFocused }) =>
      !isFocused &&
      css`
        display: none;
      `}
  }

  ${({ variant, isFocused }) => {
    switch (variant) {
      case "classic":
        return css`
          --ck-focus-ring: 1px solid var(--ck-color-base-border);

          .ck.ck-editor {
            border-radius: var(--ck-border-radius);
            ${isFocused && glow("outline")}
          }
        `;

      case "inline":
        return css`
          --ck-spacing-large: 0.6rem;

          .ck.ck-editor__editable_inline {
            border: 1px solid var(--ck-color-base-border);
            ${isFocused && glow("outline")}
          }
        `;

      case "seamless":
        return css`
          --ck-border-radius: 0;
          --ck-focus-ring: none;
          --ck-color-base-background: none;

          .ck.ck-editor__editable_inline {
            border: none;
          }

          .ck.ck-toolbar {
            border: none;
          }

          .ck.ck-editor__top .ck-sticky-panel .ck.ck-toolbar {
            border-bottom: 1px solid var(--ck-color-base-border);
          }

          .ck.ck-content {
            flex-grow: 1;
            word-break: break-word;
          }

          /*
            Handle height-filling for seamless editors with own toolbar
            (which use classic editor rendering)
          */
          .ck.ck-editor {
            height: 100%;
            display: flex;
            flex-direction: column;

            .ck.ck-editor__main {
              flex-grow: 1;
              display: flex;
            }
          }

          height: 100%;
          display: flex;
          flex-direction: column;

          ${isFocused &&
          css`
            --ck-inner-shadow: 0px 0px 0.6rem 0.1rem rgba(0, 0, 0, 0.08) inset;
            background-color: var(--rce-color-seamless-focus-background);
          `};
        `;
    }
  }}
`;

export default EditorStyles;
